import {
  GRAVITY_TILES_PER_S2,
  JUMP_APEX_TILES,
  JUMP_SPEED_TILES_PER_S,
  MOVE_SPEED_TILES_PER_S,
  PLAYER_HEIGHT_TILES,
  PLAYER_WIDTH_TILES,
  SOLVER_JUMP_MARGIN_TILES,
  SOLVER_REPEL_LAUNCH_SPEED_TILES_PER_S,
} from '@md/shared';
import type { ParsedLevel } from '../level/parser.js';
import { noMechanisms, type SolverMechanisms } from './mechanisms.js';

/**
 * Géométrie des sauts, DÉDUITE des constantes physiques (spec §8 : « saut ≤
 * portée calculée depuis les constantes physiques »).
 *
 * Le modèle est celui de l'intégrateur du moteur : accélération constante,
 * donc position exacte en fonction du temps. Aucune valeur magique ici — si
 * la gravité change dans `shared/constants.ts`, la portée suit.
 */

/** Hauteur maximale franchissable d'un saut, en tuiles entières. */
export const MAX_JUMP_UP_TILES = Math.floor(JUMP_APEX_TILES);

/** Sommet d'un saut PROPULSÉ par répulsion, en tuiles. */
export const REPEL_APEX_TILES =
  (SOLVER_REPEL_LAUNCH_SPEED_TILES_PER_S * SOLVER_REPEL_LAUNCH_SPEED_TILES_PER_S) /
  (2 * GRAVITY_TILES_PER_S2);

/** Hauteur maximale franchissable d'un saut propulsé, en tuiles entières. */
export const MAX_REPEL_UP_TILES = Math.floor(REPEL_APEX_TILES);

/** Chute maximale considérée par le graphe, en tuiles. */
export const MAX_FALL_TILES = 16;

/**
 * Durée de vol avant d'atteindre une hauteur relative `dy` (vers le BAS, en
 * tuiles), pour une impulsion verticale initiale `launchSpeed`.
 *
 * `null` quand la cible est plus haute que le sommet atteignable.
 */
export function flightTime(dy: number, launchSpeed: number): number | null {
  const discriminant = launchSpeed * launchSpeed + 2 * GRAVITY_TILES_PER_S2 * dy;
  if (discriminant < 0) {
    return null;
  }
  return (launchSpeed + Math.sqrt(discriminant)) / GRAVITY_TILES_PER_S2;
}

/** Distance horizontale franchissable pour un dénivelé `dy` (bas positif). */
export function horizontalReach(dy: number, launchSpeed: number): number {
  const t = flightTime(dy, launchSpeed);
  if (t === null) {
    return 0;
  }
  return Math.max(0, MOVE_SPEED_TILES_PER_S * t - SOLVER_JUMP_MARGIN_TILES);
}

/** Vitesse d'impulsion associée à un type de départ. */
export const LAUNCH_SPEEDS = {
  /** Saut complet. */
  jump: JUMP_SPEED_TILES_PER_S,
  /** Marche dans le vide, sans saut. */
  fall: 0,
  /** Saut propulsé par la répulsion mutuelle (concept §3.1). */
  repel: SOLVER_REPEL_LAUNCH_SPEED_TILES_PER_S,
} as const;

export type LaunchKind = keyof typeof LAUNCH_SPEEDS;

/** Boîte du robot à partir de la position de ses PIEDS et de son centre X. */
function bodyBox(
  centerX: number,
  feetY: number,
): {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
} {
  return {
    x0: centerX - PLAYER_WIDTH_TILES / 2,
    x1: centerX + PLAYER_WIDTH_TILES / 2,
    y0: feetY - PLAYER_HEIGHT_TILES,
    y1: feetY,
  };
}

/** Une boîte totalement bloquée (aucune valeur de masque ne la débloque). */
const BLOCKED = -1;

/**
 * Signaux exigés pour que la boîte passe, ou `BLOCKED`.
 *
 * `blockOneWay` reproduit la règle des plateformes fines : elles laissent
 * passer par le dessous, mais arrêtent une descente. Un robot qui retombe ne
 * peut donc pas traverser une passerelle. Une tuile couverte par une PORTE est
 * traversable au prix de son signal (`gateMaskAt`).
 */
function boxGates(
  level: ParsedLevel,
  mechanisms: SolverMechanisms,
  centerX: number,
  feetY: number,
  lethalTiles: readonly string[],
  epsilon: number,
  blockOneWay: boolean,
): number {
  const box = bodyBox(centerX, feetY);
  const tx0 = Math.floor(box.x0 + epsilon);
  const tx1 = Math.floor(box.x1 - epsilon);
  const ty0 = Math.floor(box.y0 + epsilon);
  const ty1 = Math.floor(box.y1 - epsilon);
  let mask = 0;
  for (let ty = ty0; ty <= ty1; ty += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const tile = level.tileAt(tx, ty);
      if (tile.solid || tile.lethal || lethalTiles.includes(tile.char)) {
        return BLOCKED;
      }
      if (mechanisms.blockedAt(tx, ty)) {
        return BLOCKED;
      }
      if (blockOneWay && tile.oneWay) {
        return BLOCKED;
      }
      mask |= mechanisms.gateMaskAt(tx, ty);
    }
  }
  return mask;
}

export interface TrajectoryQuery {
  readonly level: ParsedLevel;
  readonly lethalTiles: readonly string[];
  readonly fromTx: number;
  readonly fromTy: number;
  readonly toTx: number;
  readonly toTy: number;
  readonly launch: LaunchKind;
  /**
   * Le départ se fait-il depuis un sol ? Une chute (sans saut) commence alors
   * au BORD de la plateforme : sans cela le sol de départ serait compté comme
   * obstacle dès le premier échantillon.
   */
  readonly fromGround: boolean;
  /** Modèle des mécanismes ; absent = aucun (tuiles seules). */
  readonly mechanisms?: SolverMechanisms;
}

/** Pas d'échantillonnage de la trajectoire, en secondes (déterministe). */
const SAMPLE_DT = 1 / 60;

/**
 * Signaux exigés par la trajectoire `from → to`, ou `null` si elle est
 * physiquement irréalisable.
 *
 * On rejoue la politique du pilote de l'étage B : impulsion (ou pas), direction
 * maintenue à pleine vitesse jusqu'à l'abscisse cible, puis chute. Le robot doit
 * arriver à la bonne colonne AVANT de retomber au niveau de la cible, et sa
 * boîte ne doit rencontrer ni solide ni tuile mortelle en chemin — une porte
 * traversée ajoute son signal au masque renvoyé.
 */
export function trajectoryGates(query: TrajectoryQuery): number | null {
  const { level, lethalTiles, fromTx, fromTy, toTx, toTy } = query;
  const mechanisms = query.mechanisms ?? noMechanisms();
  const dy = toTy - fromTy;
  const maxUp = query.launch === 'repel' ? MAX_REPEL_UP_TILES : MAX_JUMP_UP_TILES;
  if (dy < -maxUp || dy > MAX_FALL_TILES) {
    return null;
  }
  const launchSpeed = LAUNCH_SPEEDS[query.launch];
  const totalTime = flightTime(dy, launchSpeed);
  if (totalTime === null) {
    return null;
  }

  // Une chute depuis un sol démarre au bord de la plateforme : le robot a
  // d'abord marché jusque-là, c'est ce que reproduit le pilote du rejeu.
  const ledgeOffset =
    query.launch === 'fall' && query.fromGround ? 0.5 + PLAYER_WIDTH_TILES / 2 : 0;
  const rawDx = toTx - fromTx;
  const sign = rawDx === 0 ? 0 : Math.sign(rawDx);
  if (ledgeOffset > 0 && sign === 0) {
    return null; // on ne tombe pas sur place depuis une plateforme.
  }
  const startX = fromTx + 0.5 + sign * ledgeOffset;
  const startFeet = fromTy + 1;
  const dx = toTx + 0.5 - startX;
  if (sign !== 0 && Math.sign(dx) !== sign) {
    return null; // le bord dépasse déjà la cible : ce n'est pas une chute.
  }
  if (Math.abs(dx) > horizontalReach(dy, launchSpeed)) {
    return null;
  }
  const steps = Math.max(1, Math.ceil(totalTime / SAMPLE_DT));
  let mask = 0;
  for (let i = 0; i <= steps; i += 1) {
    const t = (totalTime * i) / steps;
    const travelled = Math.min(MOVE_SPEED_TILES_PER_S * t, Math.abs(dx));
    const centerX = startX + sign * travelled;
    const feetY = startFeet - launchSpeed * t + (GRAVITY_TILES_PER_S2 * t * t) / 2;
    // Le dernier échantillon touche le sol d'arrivée : on l'exclut du test en
    // remontant d'un cheveu, sinon toute arrivée serait « bloquée ».
    const probeFeet = i === steps ? feetY - 1e-3 : feetY;
    const descending = GRAVITY_TILES_PER_S2 * t > launchSpeed;
    const gates = boxGates(
      level,
      mechanisms,
      centerX,
      probeFeet,
      lethalTiles,
      1e-6,
      descending && i < steps,
    );
    if (gates === BLOCKED) {
      return null;
    }
    mask |= gates;
  }
  return mask;
}

/** La trajectoire `from → to` est-elle réalisable (portes ignorées) ? */
export function trajectoryReaches(query: TrajectoryQuery): boolean {
  return trajectoryGates(query) !== null;
}
